import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import AccountingToolPage from '@/components/AccountingToolPage'
import { toast } from '@/store/toastStore'
import VendorDetailsFilterBar from '@/components/VendorDetailsFilterBar'
import VendorSpendSidebar from '@/components/VendorSpendSidebar'
import { refreshVendorLtmStats } from '@/services/vendorDetails'
import { getSettings, updateSetting } from '@/services/settings'
import { formatApiError } from '@/utils/apiError'
import {
  fetchVendorDetailsIntoStore,
  useVendorDetailsStore,
} from '@/store/vendorDetailsStore'
import {
  applyVendorTableFilters,
  buildVendorFilterOptions,
  countActiveVendorFilters,
  filterVendorsByText,
  generateVendorFilterPills,
  removeVendorFilterPill,
  vendorLtmStatusLabel,
  type VendorTableFilters,
} from '@/pages/vendorDetailsFilters'
import {
  DEFAULT_VISIBLE_COLUMNS,
  DEFAULT_WIDTHS,
  REFRESH_TITLE,
  VENDOR_COLUMNS,
  VENDOR_DETAILS_PAGE_KEY,
  VENDOR_DETAILS_VISIBLE_COLUMNS_KEY,
  VENDOR_FIELD_DEFINITIONS,
  parseVisibleColumns,
  renderVendorTableCell,
  type VendorColumnKey,
} from '@/pages/vendorDetailsTableConfig'
import {
  ActiveFilterPillBar,
  ColumnPicker,
  ColumnResizeHandle,
  DrawerShell,
  SearchInput,
  SortBuilder,
  TablePageShell,
  TableToolbar,
  applySorts,
  readStoredColumnWidths,
  saveColumnWidths,
  useColumnWidths,
  type ColumnVisibility,
  type SortSpec,
} from '@/tableControls'

const ALL_COLUMN_KEYS = VENDOR_COLUMNS.map((col) => col.key)
const PINNED_COLUMNS = ['display_name'] as const
const LEGACY_WIDTH_KEYS = [
  'bookkeeper:vendor-details-widths-v2',
  'bookkeeper:vendor-details-widths',
] as const

function escapeCsvCell(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function migrateLegacyColumnWidths(): void {
  const existing = readStoredColumnWidths(VENDOR_DETAILS_PAGE_KEY)
  if (Object.keys(existing).length > 0) return
  for (const legacyKey of LEGACY_WIDTH_KEYS) {
    try {
      const raw = localStorage.getItem(legacyKey)
      if (!raw) continue
      const parsed = JSON.parse(raw) as Record<string, unknown>
      const widths: Record<string, number> = {}
      for (const key of ALL_COLUMN_KEYS) {
        const value = parsed[key]
        if (typeof value === 'number' && Number.isFinite(value)) {
          widths[key] = value
        }
      }
      if (Object.keys(widths).length > 0) {
        saveColumnWidths(VENDOR_DETAILS_PAGE_KEY, widths)
      }
      return
    } catch {
      /* try next legacy key */
    }
  }
}

export default function VendorDetails() {
  const vendors = useVendorDetailsStore((s) => s.vendors)
  const ltmStart = useVendorDetailsStore((s) => s.ltmStart)
  const ltmEnd = useVendorDetailsStore((s) => s.ltmEnd)
  const ltmSyncedAt = useVendorDetailsStore((s) => s.ltmSyncedAt)
  const search = useVendorDetailsStore((s) => s.search)
  const tableFilters = useVendorDetailsStore((s) => s.tableFilters)
  const setSearch = useVendorDetailsStore((s) => s.setSearch)
  const setTableFilters = useVendorDetailsStore((s) => s.setTableFilters)
  const clearTableFilters = useVendorDetailsStore((s) => s.clearTableFilters)
  const vendorCount = useVendorDetailsStore((s) => s.vendors.length)

  const [loading, setLoading] = useState(
    () => useVendorDetailsStore.getState().vendors.length === 0
  )
  const [refreshing, setRefreshing] = useState(false)
  const [visibleColumns, setVisibleColumns] = useState<VendorColumnKey[]>(DEFAULT_VISIBLE_COLUMNS)
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [showSort, setShowSort] = useState(false)
  const [showColumns, setShowColumns] = useState(false)
  const [sortSpecs, setSortSpecs] = useState<SortSpec[]>([
    { field: 'gross_spend_ltm', direction: 'desc' },
  ])

  useEffect(() => {
    migrateLegacyColumnWidths()
  }, [])

  const { getWidth, startResize, getStickyLeft } = useColumnWidths({
    pageKey: VENDOR_DETAILS_PAGE_KEY,
    columnKeys: ALL_COLUMN_KEYS,
    defaults: DEFAULT_WIDTHS,
  })

  const visibleColumnDefs = useMemo(
    () => VENDOR_COLUMNS.filter((col) => visibleColumns.includes(col.key)),
    [visibleColumns]
  )

  const columnVisibility = useMemo<ColumnVisibility>(() => {
    const visibility: ColumnVisibility = {}
    for (const col of VENDOR_COLUMNS) {
      visibility[col.key] = visibleColumns.includes(col.key)
    }
    return visibility
  }, [visibleColumns])

  const searchFiltered = useMemo(
    () => filterVendorsByText(vendors, search),
    [vendors, search]
  )

  const filterOptions = useMemo(() => buildVendorFilterOptions(searchFiltered), [searchFiltered])

  const filtered = useMemo(
    () => applyVendorTableFilters(searchFiltered, tableFilters),
    [searchFiltered, tableFilters]
  )

  const displayed = useMemo(
    () => applySorts(filtered, sortSpecs, VENDOR_FIELD_DEFINITIONS),
    [filtered, sortSpecs]
  )

  const activeFilterCount = countActiveVendorFilters(tableFilters)
  const filterPills = useMemo(
    () => generateVendorFilterPills(tableFilters),
    [tableFilters]
  )

  const load = useCallback(async (refresh = false) => {
    try {
      setLoading(true)
      await fetchVendorDetailsIntoStore(refresh)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load vendors'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (vendorCount > 0) return
    void load()
  }, [vendorCount, load])

  useEffect(() => {
    void getSettings()
      .then(({ settings }) => {
        const raw = settings[VENDOR_DETAILS_VISIBLE_COLUMNS_KEY]?.value
        setVisibleColumns(parseVisibleColumns(raw))
      })
      .catch(() => {
        setVisibleColumns(DEFAULT_VISIBLE_COLUMNS)
      })
  }, [])

  function updateTableFilter<K extends keyof VendorTableFilters>(
    key: K,
    value: VendorTableFilters[K]
  ) {
    setTableFilters((prev) => ({ ...prev, [key]: value }))
  }

  async function persistVisibleColumns(nextColumns: VendorColumnKey[]) {
    const withRequired = nextColumns.includes('display_name')
      ? nextColumns
      : (['display_name', ...nextColumns] as VendorColumnKey[])
    setVisibleColumns(withRequired)
    try {
      await updateSetting(VENDOR_DETAILS_VISIBLE_COLUMNS_KEY, {
        value: JSON.stringify(withRequired),
        description: 'Visible columns on the Vendors table (shared for all users)',
      })
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save column preferences'))
    }
  }

  function handleColumnVisibilityChange(visibility: ColumnVisibility) {
    const ordered = VENDOR_COLUMNS.map((col) => col.key).filter(
      (key) => visibility[key] !== false
    )
    void persistVisibleColumns(ordered)
  }

  async function handleRefresh() {
    try {
      setRefreshing(true)
      await fetchVendorDetailsIntoStore(true)
      const result = await refreshVendorLtmStats()
      await load()
      toast.success(
        `Refreshed vendors and LTM stats for ${result.vendors_with_stats} vendors (${result.ltm_start_date} – ${result.ltm_end_date})`
      )
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to refresh vendors'))
    } finally {
      setRefreshing(false)
    }
  }

  function exportCsv() {
    const headers = [
      'Vendor Name',
      'ID',
      'Company Name',
      'Marketplace',
      'Print on Check Name',
      'Status',
      'Balance',
      'Gross Spend, LTM',
      'Number of Expense Transactions, LTM',
      'Payment Method',
      'Liability Timing',
      'Payment Terms',
      'Overseen By',
      'URL',
      'Notes',
      'Tax ID',
      'Account Number',
      '1099 Vendor',
      'Email',
      'Phone',
      'Mobile',
      'Fax',
      'Website',
      'Address Line 1',
      'Address Line 2',
      'Address Line 3',
      'City',
      'State',
      'Zip',
      'Country',
    ]
    const rows = displayed.map((v) =>
      [
        v.display_name,
        v.id,
        v.company_name,
        v.marketplace,
        v.print_on_check_name,
        vendorLtmStatusLabel(v),
        String(v.balance),
        String(v.gross_spend_ltm),
        String(v.expense_txn_count_ltm),
        v.payment_method,
        v.liability_timing,
        v.term_ref,
        v.overseen_by,
        v.url,
        v.notes,
        v.tax_identifier,
        v.acct_num,
        v.vendor_1099 ? 'Yes' : 'No',
        v.primary_email,
        v.primary_phone,
        v.mobile,
        v.fax,
        v.web_url,
        v.bill_addr_line1,
        v.bill_addr_line2,
        v.bill_addr_line3,
        v.bill_addr_city,
        v.bill_addr_state,
        v.bill_addr_postal_code,
        v.bill_addr_country,
      ]
        .map(escapeCsvCell)
        .join(',')
    )
    const blob = new Blob([[headers.join(','), ...rows].join('\n')], {
      type: 'text/csv;charset=utf-8',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'vendor-details.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const isBusy = loading || refreshing
  const showingSubset =
    filtered.length !== vendors.length ||
    search.trim().length > 0 ||
    activeFilterCount > 0

  const selectedVendor = useMemo(
    () => vendors.find((v) => v.id === selectedVendorId) ?? null,
    [vendors, selectedVendorId]
  )

  const analyticsDefaultStart = ltmStart || ''
  const analyticsDefaultEnd = ltmEnd || ''

  const pinnedKeys = [...PINNED_COLUMNS]

  const headerStyle = useCallback(
    (columnKey: string): CSSProperties => {
      const width = getWidth(columnKey)
      const isPinned = PINNED_COLUMNS.includes(columnKey as (typeof PINNED_COLUMNS)[number])
      const style: CSSProperties = {
        width,
        minWidth: width,
        maxWidth: width,
        boxSizing: 'border-box',
        position: 'sticky',
        top: 0,
        zIndex: isPinned ? 20 : 10,
      }
      if (isPinned) {
        style.left = getStickyLeft(pinnedKeys, columnKey)
      }
      return style
    },
    [getStickyLeft, getWidth, pinnedKeys]
  )

  const cellStyle = useCallback(
    (columnKey: string): CSSProperties => {
      const width = getWidth(columnKey)
      const isPinned = PINNED_COLUMNS.includes(columnKey as (typeof PINNED_COLUMNS)[number])
      const style: CSSProperties = {
        width,
        minWidth: width,
        maxWidth: width,
        boxSizing: 'border-box',
      }
      if (isPinned) {
        return {
          ...style,
          position: 'sticky',
          left: getStickyLeft(pinnedKeys, columnKey),
          zIndex: 5,
        }
      }
      return style
    },
    [getStickyLeft, getWidth, pinnedKeys]
  )

  const pinnedHeaderClass = (columnKey: string) => {
    if (!PINNED_COLUMNS.includes(columnKey as (typeof PINNED_COLUMNS)[number])) return ''
    return 'table-sticky-pinned table-sticky-cell table-locked-edge'
  }

  const pinnedCellClass = (columnKey: string) => {
    if (!PINNED_COLUMNS.includes(columnKey as (typeof PINNED_COLUMNS)[number])) return ''
    return 'table-sticky-pinned table-sticky-cell table-locked-edge'
  }

  const visibleTableWidth = useMemo(() => {
    return visibleColumnDefs.reduce((sum, col) => sum + getWidth(col.key), 0)
  }, [getWidth, visibleColumnDefs])

  return (
    <AccountingToolPage
      title="Vendors"
      description="Enhanced metadata view of QuickBooks vendors"
      scrollContent={false}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {loading && vendors.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
            Loading vendors…
          </div>
        ) : (
          <TablePageShell
            rowCount={
              <div className="mb-2 text-xs text-gray-500 sm:text-sm">
                <span className="font-medium text-gray-700">
                  {filtered.length.toLocaleString()} vendor{filtered.length !== 1 ? 's' : ''}
                </span>
                {showingSubset ? (
                  <span className="ml-1">(of {vendors.length.toLocaleString()} total)</span>
                ) : null}
                <span className="ml-1">
                  · LTM window: {ltmStart || '—'} – {ltmEnd || '—'}
                </span>
                <span>
                  {ltmSyncedAt
                    ? ` · Stats synced ${new Date(ltmSyncedAt).toLocaleString()}`
                    : ' · Stats not cached — click Refresh'}
                </span>
              </div>
            }
            toolbarLeft={
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search vendors…"
                className="max-w-md flex-1"
              />
            }
            toolbarRight={
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  title={REFRESH_TITLE}
                  onClick={() => void handleRefresh()}
                  disabled={isBusy}
                >
                  <ArrowPathIcon className={`h-4 w-4 ${isBusy ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
                <TableToolbar
                  filterCount={activeFilterCount}
                  sortCount={sortSpecs.length}
                  showFilters={showFilters}
                  showSort={showSort}
                  showColumns={showColumns}
                  onToggleFilters={() => setShowFilters((open) => !open)}
                  onToggleSort={() => setShowSort((open) => !open)}
                  onToggleColumns={() => setShowColumns((open) => !open)}
                  onExport={exportCsv}
                />
              </div>
            }
            filterPills={
              <ActiveFilterPillBar
                pills={filterPills}
                onRemovePill={(pillId) =>
                  setTableFilters((prev) => removeVendorFilterPill(prev, pillId))
                }
                onClearAll={clearTableFilters}
              />
            }
          >
            <table
              className="table-forecast w-full border-separate border-spacing-0"
              style={{ width: visibleTableWidth, minWidth: visibleTableWidth }}
            >
              <thead>
                <tr>
                  {visibleColumnDefs.map((col) => (
                      <th
                        key={col.key}
                        className={`relative ${col.align === 'right' ? 'text-right' : ''} ${pinnedHeaderClass(col.key)}`}
                        style={headerStyle(col.key)}
                        title={col.title}
                      >
                        <span className="inline-flex items-center gap-1 pr-2">{col.label}</span>
                        <ColumnResizeHandle
                          columnKey={col.key}
                          label={col.label}
                          onStartResize={startResize}
                        />
                      </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={visibleColumnDefs.length}
                      className="py-12 text-center text-sm text-gray-600"
                    >
                      {vendors.length === 0
                        ? 'No vendors loaded. Click Refresh to sync from QuickBooks.'
                        : 'No vendors match your search or filters.'}
                    </td>
                  </tr>
                ) : null}
                {displayed.map((vendor) => (
                  <tr
                    key={vendor.id}
                    onClick={() => setSelectedVendorId(vendor.id)}
                    className={`cursor-pointer ${
                      selectedVendorId === vendor.id ? 'row-cursor' : ''
                    }`}
                  >
                    {visibleColumnDefs.map((col) =>
                      renderVendorTableCell(
                        vendor,
                        col.key,
                        cellStyle as (key: string) => CSSProperties,
                        pinnedCellClass(col.key)
                      )
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </TablePageShell>
        )}
      </div>

      {showFilters ? (
        <DrawerShell title="Filters" onClose={() => setShowFilters(false)}>
          <VendorDetailsFilterBar
            filters={tableFilters}
            options={filterOptions}
            onChange={updateTableFilter}
          />
        </DrawerShell>
      ) : null}

      <SortBuilder
        open={showSort}
        fields={VENDOR_FIELD_DEFINITIONS}
        sorts={sortSpecs}
        onChange={setSortSpecs}
        onClose={() => setShowSort(false)}
        onClearAll={() => setSortSpecs([])}
      />

      <ColumnPicker
        open={showColumns}
        fields={VENDOR_FIELD_DEFINITIONS}
        visibility={columnVisibility}
        onChange={handleColumnVisibilityChange}
        onClose={() => setShowColumns(false)}
        onReset={() => void persistVisibleColumns(DEFAULT_VISIBLE_COLUMNS)}
      />

      <VendorSpendSidebar
        vendor={selectedVendor}
        open={selectedVendor != null}
        onClose={() => setSelectedVendorId(null)}
        defaultStartDate={analyticsDefaultStart}
        defaultEndDate={analyticsDefaultEnd}
      />
    </AccountingToolPage>
  )
}
